import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import SuperAdminSubLicenze from "../../components/SuperAdminSubLicenze";
import {
  Plus, Pencil, Trash2, PackageOpen, Euro, BadgeCheck, AlertTriangle,
  Clock, Ban, CalendarCheck2, Receipt, TrendingUp, ChevronRight,
} from "lucide-react";

const CICLI = [
  { v: "mensile", l: "Mensile" },
  { v: "trimestrale", l: "Trimestrale" },
  { v: "semestrale", l: "Semestrale" },
  { v: "annuale", l: "Annuale" },
  { v: "una_tantum", l: "Una tantum" },
];

const METODI = [
  { v: "bonifico", l: "Bonifico" },
  { v: "carta", l: "Carta" },
  { v: "contanti", l: "Contanti" },
  { v: "assegno", l: "Assegno" },
  { v: "altro", l: "Altro" },
];

const EMPTY_PIANO = {
  id: null, nome: "", descrizione: "",
  modello_prezzo: "flat",
  prezzo_mensile: 0, prezzo_annuo: 0,
  prezzo_per_condominio_annuo: 0, iva_percentuale: 22,
  max_condomini: 0, max_fornitori: 0, max_utenti: 0,
  moduli_attivi: [], ordine: 100, attivo: true,
};

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; }
};

const StatoBadge = ({ stato, giorni }) => {
  const map = {
    attiva:      { cls: "bg-green-100 text-green-800 border-green-300", icon: BadgeCheck, label: "Attiva" },
    in_scadenza: { cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock, label: `In scadenza (${giorni ?? "?"}gg)` },
    scaduta:     { cls: "bg-red-100 text-red-800 border-red-300", icon: AlertTriangle, label: "Scaduta" },
    sospesa:     { cls: "bg-slate-200 text-slate-700 border-slate-300", icon: Ban, label: "Sospesa" },
    prova:       { cls: "bg-blue-100 text-blue-800 border-blue-300", icon: Clock, label: "Prova" },
    nessuna:     { cls: "bg-slate-100 text-slate-500 border-slate-300", icon: PackageOpen, label: "Nessun piano" },
  };
  const c = map[stato] || map.nessuna;
  const Ic = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${c.cls}`}
          data-testid={`licenza-stato-${stato}`}>
      <Ic className="w-3.5 h-3.5" /> {c.label}
    </span>
  );
};

export default function TabListino({ tenants, onReload }) {
  const [piani, setPiani] = useState(null);
  const [riepilogo, setRiepilogo] = useState(null);
  const [openPiano, setOpenPiano] = useState(false);
  const [pianoForm, setPianoForm] = useState(EMPTY_PIANO);
  const [busy, setBusy] = useState(false);
  const [openLicenza, setOpenLicenza] = useState(null);   // tenant selezionato
  const [licForm, setLicForm] = useState({ piano_id: "", ciclo_billing: "annuale", attivazione_at: "", scadenza_at: "", prezzo: "", note: "" });
  const [openPag, setOpenPag] = useState(null);           // tenant selezionato
  const [pagamenti, setPagamenti] = useState([]);
  const [pagForm, setPagForm] = useState({ data_pagamento: "", importo: "", metodo: "bonifico", riferimento: "", note: "" });
  const [openRinnovo, setOpenRinnovo] = useState(null);   // {tenant, preview}
  const [expanded, setExpanded] = useState(() => new Set()); // Iter122cb · card licenza espanse
  const toggleExpanded = (tid) => setExpanded((s) => {
    const n = new Set(s); n.has(tid) ? n.delete(tid) : n.add(tid); return n;
  });

  const load = async () => {
    try {
      const [p, r] = await Promise.all([
        api.get("/superadmin/piani"),
        api.get("/superadmin/listino/riepilogo"),
      ]);
      setPiani(p.data); setRiepilogo(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore di caricamento listino");
    }
  };
  useEffect(() => { load(); }, []);

  const savePiano = async () => {
    if (!pianoForm.nome.trim()) { toast.error("Il nome del piano è obbligatorio"); return; }
    setBusy(true);
    try {
      if (pianoForm.id) await api.put(`/superadmin/piani/${pianoForm.id}`, pianoForm);
      else await api.post("/superadmin/piani", pianoForm);
      toast.success(pianoForm.id ? "Piano aggiornato" : "Piano creato");
      setOpenPiano(false); setPianoForm(EMPTY_PIANO); load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const deletePiano = async (p) => {
    if (!window.confirm(`Eliminare il piano "${p.nome}"? Se assegnato a uno o più studi non sarà possibile.`)) return;
    try {
      await api.delete(`/superadmin/piani/${p.id}`);
      toast.success("Piano eliminato"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita"); }
  };

  const openLicenzaFor = (t) => {
    const l = t.licenza || {};
    setLicForm({
      piano_id: l.piano_id || (piani?.[0]?.id ?? ""),
      ciclo_billing: l.ciclo_billing || "annuale",
      attivazione_at: l.attivazione_at || new Date().toISOString().slice(0, 10),
      scadenza_at: l.scadenza_at || "",
      prezzo: l.prezzo ?? "",
      note: l.note || "",
    });
    setOpenLicenza(t);
  };

  const saveLicenza = async () => {
    if (!licForm.piano_id) { toast.error("Seleziona un piano"); return; }
    if (!licForm.attivazione_at) { toast.error("Data di attivazione mancante"); return; }
    setBusy(true);
    try {
      const body = { ...licForm };
      if (body.prezzo === "" || body.prezzo === null) delete body.prezzo;
      else body.prezzo = Number(body.prezzo);
      if (!body.scadenza_at) delete body.scadenza_at;
      await api.put(`/superadmin/tenants/${openLicenza.id}/licenza`, body);
      toast.success("Licenza aggiornata");
      setOpenLicenza(null); load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const openPagFor = async (t) => {
    setOpenPag(t); setPagamenti([]);
    setPagForm({ data_pagamento: new Date().toISOString().slice(0, 10), importo: "", metodo: "bonifico", riferimento: "", note: "" });
    try {
      const { data } = await api.get(`/superadmin/tenants/${t.id}/pagamenti`);
      setPagamenti(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore lettura pagamenti"); }
  };

  const addPagamento = async () => {
    if (!pagForm.data_pagamento || !pagForm.importo) { toast.error("Data e importo obbligatori"); return; }
    setBusy(true);
    try {
      await api.post(`/superadmin/tenants/${openPag.id}/pagamenti`, {
        ...pagForm, importo: Number(pagForm.importo),
      });
      toast.success("Pagamento registrato");
      openPagFor(openPag); load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const deletePagamento = async (p) => {
    if (!window.confirm(`Eliminare il pagamento del ${fmtDate(p.data_pagamento)} da ${eur(p.importo)}?`)) return;
    try {
      await api.delete(`/superadmin/tenants/${openPag.id}/pagamenti/${p.id}`);
      toast.success("Pagamento eliminato");
      openPagFor(openPag); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const openRinnovoFor = async (t) => {
    try {
      const { data } = await api.get(`/superadmin/tenants/${t.id}/licenza/preview-rinnovo`);
      setOpenRinnovo({ tenant: t, preview: data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile calcolare il rinnovo"); }
  };

  const doRinnova = async () => {
    if (!openRinnovo) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/tenants/${openRinnovo.tenant.id}/licenza/rinnova`);
      toast.success("Licenza rinnovata: canone aggiornato e scadenza prorogata");
      setOpenRinnovo(null); load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Rinnovo non riuscito"); }
    finally { setBusy(false); }
  };

  // Iter122bz · emetti pro-forma. Iter122cj · se modalità = condominio → bulk sub-licenze.
  const emettiProforma = async (t) => {
    if (t.modalita_fatturazione === "condominio") {
      if (!window.confirm(`Emettere N pro-forma per lo studio "${t.nome}" (una per ciascun condominio con sub-licenza attiva)?`)) return;
      try {
        const { data } = await api.post(`/superadmin/tenants/${t.id}/proforma-bulk`, { note: "" });
        const msg = `Emesse ${data.emesse} pro-forma${data.errori.length ? ` · ${data.errori.length} errori` : ""}`;
        data.emesse > 0 ? toast.success(msg) : toast.error(msg);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore emissione bulk"); }
      return;
    }
    if (!window.confirm(`Emettere pro-forma per "${t.nome}"?\n\nVerrà creato il documento contabile con i dati fiscali dello studio destinatario.`)) return;
    try {
      const { data } = await api.post(`/superadmin/tenants/${t.id}/proforma`, { note: "" });
      toast.success(`Pro-forma ${data.numero} emessa · Totale € ${data.totale}`);
      const res = await api.get(`/superadmin/fatture/${data.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${data.numero}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore emissione"); }
  };

  // Iter122bx · revoca licenza
  const revocaLicenza = async (t) => {
    if (!window.confirm(`Revocare la licenza di "${t.nome}"?\n\nLo studio resta attivo ma senza piano assegnato. I pagamenti storici sono conservati.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${t.id}/licenza`);
      toast.success("Licenza revocata");
      load(); onReload && onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Revoca non riuscita"); }
  };


  const tot = riepilogo?.totali || {};

  return (
    <div className="space-y-6" data-testid="tab-listino">
      {/* KPI header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["MRR",  eur(tot.mrr), TrendingUp, "text-emerald-700 bg-emerald-50 border-emerald-200"],
          ["ARR",  eur(tot.arr), TrendingUp, "text-emerald-700 bg-emerald-50 border-emerald-200"],
          ["Incassato",    eur(tot.totale_incassato), Receipt, "text-brand bg-brand/5 border-brand/30"],
          ["Studi senza piano", `${tot.numero_studi_senza_piano ?? 0}/${tot.numero_studi_totali ?? 0}`, PackageOpen, "text-amber-700 bg-amber-50 border-amber-200"],
        ].map(([label, value, Ic, cls]) => (
          <Card key={label} className={`p-4 border-2 ${cls}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase font-bold tracking-wide opacity-70">{label}</div>
                <div className="text-2xl font-bold mt-1">{value}</div>
              </div>
              <Ic className="w-6 h-6 opacity-60" />
            </div>
          </Card>
        ))}
      </div>

      {/* Sezione Piani */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl font-bold text-slate-800">Piani configurati ({piani?.length ?? "…"})</h3>
          <Button data-testid="new-piano-button" onClick={() => { setPianoForm(EMPTY_PIANO); setOpenPiano(true); }}
                  className="h-11 bg-brand hover:bg-brand-dark">
            <Plus className="w-4 h-4 mr-2" /> Nuovo piano
          </Button>
        </div>
        {piani === null ? (
          <p className="text-slate-500">Caricamento...</p>
        ) : piani.length === 0 ? (
          <Card className="p-8 text-center border-dashed border-2">
            <PackageOpen className="w-10 h-10 mx-auto text-slate-400 mb-2" />
            <p className="text-slate-600">Nessun piano configurato. Crea il primo piano per iniziare a gestire le licenze commerciali.</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {piani.map((p) => (
              <Card key={p.id} className={`p-4 border-2 ${p.attivo ? "" : "opacity-60 border-dashed"}`} data-testid={`piano-card-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-display text-lg font-bold">{p.nome}</h4>
                    {p.descrizione && <p className="text-xs text-slate-500 mt-0.5">{p.descrizione}</p>}
                  </div>
                  {!p.attivo && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">DISATTIVO</span>}
                </div>
                <div className="mt-3 flex items-baseline gap-2 text-brand">
                  {p.modello_prezzo === "per_condominio" ? (
                    <>
                      <span className="text-2xl font-bold">{eur(p.prezzo_per_condominio_annuo)}</span>
                      <span className="text-xs text-slate-500">/anno · per condominio · IVA {p.iva_percentuale}%</span>
                    </>
                  ) : (
                    <>
                      <span className="text-2xl font-bold">{eur(p.prezzo_annuo)}</span>
                      <span className="text-xs text-slate-500">/anno · {eur(p.prezzo_mensile)}/mese</span>
                    </>
                  )}
                </div>
                <div className="mt-1">
                  <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded ${p.modello_prezzo === "per_condominio" ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700"}`}>
                    {p.modello_prezzo === "per_condominio" ? "PER CONDOMINIO" : "PREZZO FISSO"}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
                  <div className="rounded bg-slate-50 py-1.5"><b>{p.max_condomini || "∞"}</b><br/>Condomini</div>
                  <div className="rounded bg-slate-50 py-1.5"><b>{p.max_fornitori || "∞"}</b><br/>Fornitori</div>
                  <div className="rounded bg-slate-50 py-1.5"><b>{p.max_utenti || "∞"}</b><br/>Utenti</div>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" className="h-9 flex-1" onClick={() => { setPianoForm({ ...EMPTY_PIANO, ...p }); setOpenPiano(true); }}
                          data-testid={`piano-edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
                  </Button>
                  <Button variant="outline" className="h-9 text-red-700 border-red-300" onClick={() => deletePiano(p)}
                          data-testid={`piano-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Sezione Studi con licenza */}
      <div>
        <h3 className="font-display text-2xl font-bold text-slate-800 mb-3">Licenze attive</h3>
        {(tenants || []).length === 0 ? (
          <p className="text-slate-500">Nessuno studio.</p>
        ) : (
          <div className="space-y-2">
            {(tenants || []).map((t) => {
              const l = t.licenza || {};
              const ls = t.licenza_stato || { stato: "nessuna" };
              const ps = t.pagamenti_summary || {};
              const isOpen = expanded.has(t.id);
              const hasLic = !!l.piano_id;
              const cicloLabel = CICLI.find(c => c.v === l.ciclo_billing)?.l || l.ciclo_billing;
              const proRata = l.giorni_copertura != null && l.giorni_copertura < 365;
              return (
                <Card key={t.id} className="border-2 overflow-hidden" data-testid={`licenza-row-${t.id}`}>
                  {/* ─── Riga sintetica sempre visibile ─── */}
                  <button type="button" onClick={() => toggleExpanded(t.id)}
                          data-testid={`licenza-toggle-${t.id}`}
                          className="w-full text-left px-3 sm:px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-2 sm:gap-3 min-w-0">
                    <ChevronRight className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="font-display font-bold text-slate-800 truncate max-w-[220px] sm:max-w-none">{t.nome}</span>
                        <StatoBadge stato={ls.stato} giorni={ls.giorni_alla_scadenza} />
                        {proRata && (
                          <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">PRO-RATA</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {hasLic
                          ? <>{l.piano_nome} · {cicloLabel} · scad. <b>{fmtDate(l.scadenza_at)}</b></>
                          : <span className="italic">Nessun piano assegnato</span>}
                      </div>
                    </div>
                    {hasLic && (
                      <div className="text-right shrink-0 hidden sm:block">
                        <div className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide">Totale</div>
                        <div className="text-sm font-bold text-brand">{eur(l.totale)}</div>
                      </div>
                    )}
                  </button>

                  {/* ─── Pannello espanso ─── */}
                  {isOpen && (
                    <div className="border-t bg-slate-50/50 px-3 sm:px-4 py-3 space-y-3" data-testid={`licenza-details-${t.id}`}>
                      {hasLic ? (
                        <>
                          {/* Piano */}
                          <div>
                            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1 tracking-wide">Piano</div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              <div className="min-w-0 break-words"><b>{l.piano_nome}</b>
                                {l.modello_prezzo === "per_condominio" && <span className="ml-1 text-[10px] font-bold text-indigo-800 bg-indigo-100 px-1.5 py-0.5 rounded">×COND</span>}
                              </div>
                              <div className="text-slate-600">Ciclo: <b>{cicloLabel}</b></div>
                            </div>
                          </div>

                          {/* Periodo */}
                          <div>
                            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1 tracking-wide">Periodo</div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              <div>Attivazione: <b>{fmtDate(l.attivazione_at)}</b></div>
                              <div>Scadenza: <b>{fmtDate(l.scadenza_at)}</b></div>
                              {l.prima_attivazione_at && l.prima_attivazione_at !== l.attivazione_at && (
                                <div className="text-slate-500 text-xs">1ª attivazione: {fmtDate(l.prima_attivazione_at)}</div>
                              )}
                              {proRata && (
                                <div className="text-amber-800 bg-amber-50 rounded px-2 py-0.5 text-xs">
                                  Pro-rata {l.giorni_copertura} gg / 365 · fattore {(l.fattore_prorata || 0).toFixed(3)}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Economics */}
                          <div>
                            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1 tracking-wide">Economics</div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              {l.modello_prezzo === "per_condominio" && (
                                <div className="text-slate-600">Base: <b>{eur(l.prezzo_unitario)}</b>/cond × {l.numero_condomini_snapshot || 0}</div>
                              )}
                              <div>Imponibile: <b>{eur(l.prezzo)}</b></div>
                              <div className="text-slate-600">IVA {l.iva_percentuale || 22}%: {eur(l.iva)}</div>
                              <div>Totale: <b className="text-brand">{eur(l.totale)}</b></div>
                              <div className="text-slate-500 text-xs">Pagamenti: {ps.numero || 0} ({eur(ps.totale_incassato)})</div>
                            </div>
                          </div>

                          {l.note && (
                            <div className="text-xs text-slate-500 italic bg-white border rounded px-2 py-1.5 break-words">Note: {l.note}</div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-slate-500 italic">Nessun piano assegnato a questo studio.</p>
                      )}

                      {/* Iter122ch · Sub-licenze per condominio (Fase 2) */}
                      <div className="border-t pt-3 mt-2">
                        <SuperAdminSubLicenze tenant={t} piani={piani} />
                      </div>

                      {/* Toolbar azioni */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={() => openLicenzaFor(t)}
                                data-testid={`licenza-edit-${t.id}`} disabled={!piani || piani.length === 0}>
                          <CalendarCheck2 className="w-4 h-4 mr-1" /> {hasLic ? "Rinnova/Cambia" : "Assegna piano"}
                        </Button>
                        {hasLic && (
                          <Button size="sm" variant="outline" className="h-9 border-emerald-300 text-emerald-700"
                                  onClick={() => openRinnovoFor(t)} data-testid={`preview-rinnovo-${t.id}`}>
                            <TrendingUp className="w-4 h-4 mr-1" /> Preview rinnovo
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-9" onClick={() => emettiProforma(t)}
                                data-testid={`emetti-proforma-${t.id}`}
                                disabled={t.modalita_fatturazione === "condominio" ? false : !hasLic}>
                          <Receipt className="w-4 h-4 mr-1" />
                          {t.modalita_fatturazione === "condominio" ? "Emetti bulk (per condominio)" : "Emetti pro-forma"}
                        </Button>
                        {hasLic && (
                          <Button size="sm" variant="outline" className="h-9 text-red-600 border-red-300 hover:bg-red-50 ml-auto"
                                  onClick={() => revocaLicenza(t)} data-testid={`licenza-revoca-${t.id}`}
                                  title="Revoca licenza · lo studio resta attivo senza piano">
                            <Trash2 className="w-4 h-4 mr-1" /> Revoca
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog: Piano */}
      <Dialog open={openPiano} onOpenChange={setOpenPiano}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{pianoForm.id ? "Modifica piano" : "Nuovo piano"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome piano *</Label>
              <Input data-testid="piano-nome" value={pianoForm.nome}
                     onChange={(e) => setPianoForm({ ...pianoForm, nome: e.target.value })} className="h-11 mt-1"
                     placeholder="Es. Base / Pro / Studio" /></div>
            <div><Label>Descrizione (opzionale)</Label>
              <Textarea data-testid="piano-descrizione" value={pianoForm.descrizione}
                        onChange={(e) => setPianoForm({ ...pianoForm, descrizione: e.target.value })} className="mt-1 min-h-[70px]" /></div>
            <div>
              <Label>Modello di prezzo</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[
                  { v: "flat", label: "Prezzo fisso", desc: "Canone identico indipendentemente dai condomini" },
                  { v: "per_condominio", label: "Per condominio", desc: "Canone = N. condomini × prezzo unitario, ricalcolato al rinnovo" },
                ].map((o) => (
                  <label key={o.v} className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer ${pianoForm.modello_prezzo === o.v ? "border-brand bg-brand/5" : "border-slate-200"}`}
                         data-testid={`piano-modello-${o.v}`}>
                    <input type="radio" name="modello_prezzo" value={o.v} checked={pianoForm.modello_prezzo === o.v}
                           onChange={(e) => setPianoForm({ ...pianoForm, modello_prezzo: e.target.value })} className="mt-1" />
                    <div className="min-w-0">
                      <div className="text-sm font-bold">{o.label}</div>
                      <div className="text-[11px] text-slate-500">{o.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {pianoForm.modello_prezzo === "per_condominio" ? (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>€ per condominio / anno</Label>
                  <Input type="number" step="0.01" data-testid="piano-prezzo-per-condominio" value={pianoForm.prezzo_per_condominio_annuo}
                         onChange={(e) => setPianoForm({ ...pianoForm, prezzo_per_condominio_annuo: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>IVA %</Label>
                  <Input type="number" step="0.01" data-testid="piano-iva" value={pianoForm.iva_percentuale}
                         onChange={(e) => setPianoForm({ ...pianoForm, iva_percentuale: e.target.value })} className="h-11 mt-1" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prezzo mensile (€)</Label>
                  <Input type="number" step="0.01" data-testid="piano-prezzo-mensile" value={pianoForm.prezzo_mensile}
                         onChange={(e) => setPianoForm({ ...pianoForm, prezzo_mensile: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Prezzo annuo (€)</Label>
                  <Input type="number" step="0.01" data-testid="piano-prezzo-annuo" value={pianoForm.prezzo_annuo}
                         onChange={(e) => setPianoForm({ ...pianoForm, prezzo_annuo: e.target.value })} className="h-11 mt-1" /></div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Max condomini</Label>
                <Input type="number" data-testid="piano-max-condomini" value={pianoForm.max_condomini}
                       onChange={(e) => setPianoForm({ ...pianoForm, max_condomini: e.target.value })} className="h-11 mt-1" />
                <p className="text-[10px] text-slate-500 mt-1">0 = illimitato</p></div>
              <div><Label>Max fornitori</Label>
                <Input type="number" data-testid="piano-max-fornitori" value={pianoForm.max_fornitori}
                       onChange={(e) => setPianoForm({ ...pianoForm, max_fornitori: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>Max utenti</Label>
                <Input type="number" data-testid="piano-max-utenti" value={pianoForm.max_utenti}
                       onChange={(e) => setPianoForm({ ...pianoForm, max_utenti: e.target.value })} className="h-11 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ordine (min = primo)</Label>
                <Input type="number" data-testid="piano-ordine" value={pianoForm.ordine}
                       onChange={(e) => setPianoForm({ ...pianoForm, ordine: e.target.value })} className="h-11 mt-1" /></div>
              <div className="flex items-end gap-2">
                <input id="piano-attivo" type="checkbox" checked={!!pianoForm.attivo} data-testid="piano-attivo"
                       onChange={(e) => setPianoForm({ ...pianoForm, attivo: e.target.checked })} className="h-5 w-5 mb-3" />
                <Label htmlFor="piano-attivo" className="mb-3">Piano attivo (visibile per assegnazione)</Label>
              </div>
            </div>
            <div className="actions-row pt-2">
              <Button data-testid="piano-save" onClick={savePiano} disabled={busy} className="h-11 bg-brand hover:bg-brand-dark">
                {busy ? "Salvataggio..." : "Salva"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setOpenPiano(false)}>Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Assegnazione licenza */}
      <Dialog open={!!openLicenza} onOpenChange={(v) => !v && setOpenLicenza(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Licenza — {openLicenza?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Piano *</Label>
              <select data-testid="licenza-piano" value={licForm.piano_id}
                      onChange={(e) => setLicForm({ ...licForm, piano_id: e.target.value })}
                      className="w-full h-11 mt-1 border rounded-md px-3 bg-white">
                {(piani || []).filter(p => p.attivo).map(p => (
                  <option key={p.id} value={p.id}>{p.nome} — {eur(p.prezzo_annuo)}/anno</option>
                ))}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ciclo di fatturazione</Label>
                <select data-testid="licenza-ciclo" value={licForm.ciclo_billing}
                        onChange={(e) => setLicForm({ ...licForm, ciclo_billing: e.target.value })}
                        className="w-full h-11 mt-1 border rounded-md px-3 bg-white">
                  {CICLI.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select></div>
              <div><Label>Prezzo (override)</Label>
                <Input type="number" step="0.01" data-testid="licenza-prezzo" value={licForm.prezzo}
                       onChange={(e) => setLicForm({ ...licForm, prezzo: e.target.value })}
                       className="h-11 mt-1" placeholder="Da piano" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data attivazione *</Label>
                <Input type="date" data-testid="licenza-attivazione" value={licForm.attivazione_at}
                       onChange={(e) => setLicForm({ ...licForm, attivazione_at: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>Scadenza (auto se vuoto)</Label>
                <Input type="date" data-testid="licenza-scadenza" value={licForm.scadenza_at}
                       onChange={(e) => setLicForm({ ...licForm, scadenza_at: e.target.value })} className="h-11 mt-1" /></div>
            </div>
            <div><Label>Note (contratto, riferimenti…)</Label>
              <Textarea data-testid="licenza-note" value={licForm.note}
                        onChange={(e) => setLicForm({ ...licForm, note: e.target.value })}
                        className="mt-1 min-h-[60px]" /></div>
            <div className="actions-row pt-2">
              <Button data-testid="licenza-save" onClick={saveLicenza} disabled={busy} className="h-11 bg-brand hover:bg-brand-dark">
                {busy ? "Salvataggio..." : "Salva licenza"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setOpenLicenza(null)}>Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Pagamenti */}
      <Dialog open={!!openPag} onOpenChange={(v) => !v && setOpenPag(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Pagamenti — {openPag?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Nuovo pagamento */}
            <Card className="p-3 border-brand/30 bg-brand/5">
              <p className="text-sm font-bold text-slate-700 mb-2">Registra pagamento ricevuto</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div><Label className="text-xs">Data</Label>
                  <Input type="date" data-testid="pag-data" value={pagForm.data_pagamento}
                         onChange={(e) => setPagForm({ ...pagForm, data_pagamento: e.target.value })} className="h-10 mt-1" /></div>
                <div><Label className="text-xs">Importo (€)</Label>
                  <Input type="number" step="0.01" data-testid="pag-importo" value={pagForm.importo}
                         onChange={(e) => setPagForm({ ...pagForm, importo: e.target.value })} className="h-10 mt-1" /></div>
                <div><Label className="text-xs">Metodo</Label>
                  <select data-testid="pag-metodo" value={pagForm.metodo}
                          onChange={(e) => setPagForm({ ...pagForm, metodo: e.target.value })}
                          className="w-full h-10 mt-1 border rounded-md px-2 bg-white text-sm">
                    {METODI.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                  </select></div>
                <div><Label className="text-xs">Riferimento</Label>
                  <Input data-testid="pag-riferimento" value={pagForm.riferimento}
                         onChange={(e) => setPagForm({ ...pagForm, riferimento: e.target.value })}
                         className="h-10 mt-1" placeholder="Fattura / CRO" /></div>
              </div>
              <div className="mt-2"><Label className="text-xs">Note</Label>
                <Input data-testid="pag-note" value={pagForm.note}
                       onChange={(e) => setPagForm({ ...pagForm, note: e.target.value })} className="h-10 mt-1" /></div>
              <Button data-testid="pag-add" onClick={addPagamento} disabled={busy} className="mt-3 h-10 bg-brand hover:bg-brand-dark">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </Card>

            {/* Storico */}
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">Storico ({pagamenti.length})</p>
              {pagamenti.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Nessun pagamento ancora registrato.</p>
              ) : (
                <div className="space-y-1">
                  {pagamenti.map(p => (
                    <div key={p.id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm"
                         data-testid={`pag-row-${p.id}`}>
                      <div className="min-w-0">
                        <b className="text-brand">{eur(p.importo)}</b> · {fmtDate(p.data_pagamento)}
                        <span className="text-slate-500"> · {p.metodo}</span>
                        {p.riferimento && <span className="text-slate-500"> · {p.riferimento}</span>}
                        {p.note && <div className="text-xs text-slate-500 italic truncate">{p.note}</div>}
                      </div>
                      <Button variant="ghost" className="h-8 text-red-600" onClick={() => deletePagamento(p)}
                              data-testid={`pag-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog: Preview & conferma rinnovo */}
      <Dialog open={!!openRinnovo} onOpenChange={(v) => !v && setOpenRinnovo(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">Preview rinnovo — {openRinnovo?.tenant.nome}</DialogTitle></DialogHeader>
          {openRinnovo?.preview && (() => {
            const pv = openRinnovo.preview;
            const isXCond = pv.modello_prezzo === "per_condominio";
            const dp = pv.delta || {};
            const deltaImp = dp.imponibile || 0;
            const deltaTot = dp.totale || 0;
            const deltaCls = deltaTot > 0 ? "text-emerald-700" : deltaTot < 0 ? "text-red-700" : "text-slate-600";
            const arrow = deltaTot > 0 ? "▲" : deltaTot < 0 ? "▼" : "=";
            return (
              <div className="space-y-4">
                <Card className="p-3 bg-slate-50">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">Canone attualmente in vigore</p>
                  <div className="grid grid-cols-2 gap-y-1 text-sm">
                    {isXCond && <><div className="text-slate-500">Condomini snapshot:</div><div className="text-right"><b>{pv.attuale.numero_condomini_snapshot}</b></div>
                                <div className="text-slate-500">€ per condominio:</div><div className="text-right">{eur(pv.attuale.prezzo_unitario)}</div></>}
                    <div className="text-slate-500">Imponibile:</div><div className="text-right"><b>{eur(pv.attuale.imponibile)}</b></div>
                    <div className="text-slate-500">IVA {pv.attuale.iva_pct}%:</div><div className="text-right">{eur(pv.attuale.iva)}</div>
                    <div className="text-slate-500 font-bold">Totale:</div><div className="text-right font-bold text-brand">{eur(pv.attuale.totale)}</div>
                  </div>
                </Card>
                <Card className="p-3 border-2 border-emerald-300 bg-emerald-50">
                  <p className="text-xs font-bold text-emerald-800 uppercase mb-2">Rinnovo previsto (calcolato ora)</p>
                  <div className="grid grid-cols-2 gap-y-1 text-sm">
                    {isXCond && <><div className="text-slate-600">Condomini attuali:</div><div className="text-right"><b>{pv.rinnovo_previsto.num_condomini}</b></div>
                                <div className="text-slate-600">€ per condominio:</div><div className="text-right">{eur(pv.rinnovo_previsto.prezzo_unitario)}</div></>}
                    <div className="text-slate-600">Imponibile:</div><div className="text-right"><b>{eur(pv.rinnovo_previsto.imponibile)}</b></div>
                    <div className="text-slate-600">IVA {pv.rinnovo_previsto.iva_pct}%:</div><div className="text-right">{eur(pv.rinnovo_previsto.iva)}</div>
                    <div className="text-slate-800 font-bold">Totale:</div><div className="text-right font-bold text-emerald-800 text-lg">{eur(pv.rinnovo_previsto.totale)}</div>
                  </div>
                </Card>
                <Card className={`p-3 border-2 ${deltaTot > 0 ? "border-emerald-300 bg-emerald-50" : deltaTot < 0 ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
                  <p className="text-xs font-bold uppercase text-slate-600 mb-2">Variazione rispetto al canone attuale</p>
                  <div className={`text-sm ${deltaCls}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold">{arrow} {eur(Math.abs(deltaTot))}</span>
                      <span className="text-xs">totale (IVA inclusa)</span>
                    </div>
                    <div className="text-xs mt-1">
                      Imponibile: {arrow} {eur(Math.abs(deltaImp))}{isXCond && <>, condomini: {arrow} {Math.abs(dp.condomini || 0)}</>}
                    </div>
                  </div>
                </Card>
                <div className="text-xs text-slate-600">
                  Scadenza attuale: <b>{fmtDate(pv.scadenza_at)}</b> ·
                  Prossima scadenza dopo rinnovo: <b>{fmtDate(pv.prossima_scadenza_prevista)}</b>
                </div>
                <div className="actions-row pt-2">
                  <Button data-testid="rinnovo-conferma" onClick={doRinnova} disabled={busy}
                          className="h-11 bg-brand hover:bg-brand-dark">
                    {busy ? "Applicazione..." : "Rinnova ora e applica il nuovo canone"}
                  </Button>
                  <Button variant="outline" className="h-11" onClick={() => setOpenRinnovo(null)}>Solo preview, chiudi</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
